// R25 — THE RIGHT-RAIL DRAWER TAB, AND ITS ICON TABLE, WHICH IS THE SAME DEFECT ONE SIZE DOWN.
//
// The ONE style for every right-rail drawer tab. The roster it serves is `RAIL_LABELS` in
// `src/rail.js` — **nine drawers, nine tabs, since her RAIL RESTORE ruling reversed the More tuck**.
// Same consolidation law as NameFields and the SubjectPicker: one component, so every current and
// future drawer tab is byte-identical (size, white text, dark fill, aqua glow) automatically — and
// the phone rhythm compacts all nine at once because there is one class to compact.
//
// **THE TAB NAMES ARE NOT LISTED HERE ANY MORE, AND THAT IS DELIBERATE** (DL-2/R181): this comment
// used to name the eight it knew about, and it was still naming eight when nine were rendering. A
// prose list of a roster is a receipt nobody cashes; the roster is in one place and this file is
// held against it.
//
// HP4: the rail's icons, per the mockup — the icon sits ABOVE its label in a neat box, keyed by the
// tab's own LABEL so every tab inherits from this one component with no call-site changes, which is
// the whole reason DrawerTab exists.
//
// AND KEYING BY THE LABEL IS WHAT MAKES THIS A CROSSING (R25, and it is R24's shape): the label is a
// LITERAL at the call sites — `<DrawerTab label="Verses">` — while the icon it resolves to lives in a
// Record two hundred lines away. A label nobody put in the table falls back to a generic Sparkles
// SILENTLY: the tab still renders, still opens its drawer, and just wears the wrong face. Nothing
// throws, nothing logs, and the fallback is what hides it — a missing icon would be visible, a
// plausible one is not.
//
// The craft drawer's label is COMPUTED rather than typed (one component renders Chants, Affirmations
// and Incantations from its kind), which is why the fixture holds the table against the labels the
// call sites really pass rather than against a hand-typed list of tab names.
//
// Hookless, so `scripts/r25-reading-door.mjs` CALLS it and reads the rendered icon back.
// RAIL-9(d) — AND THE TABLE WAS ALREADY ONE ENTRY SHORT WHEN THIS ROUND OPENED, WHICH IS THE
// WARNING ABOVE ARRIVING AS A REAL DEFECT. `da25602` put a NINTH tab on the rail (My Closet) and
// this Record rostered EIGHT, so `CLOSET_TITLE` fell straight through to the generic `Sparkles` —
// the exact silent fallback this file's own header describes, on a tab she has asked to KEEP.
//
// **THE KEYS ARE THE CONSTANTS THE CALL SITES PASS, NOT LITERALS THAT MATCH THEM TODAY** (R11/R31).
// `<DrawerTab label={CLOSET_TITLE}>` resolving against a hand-typed `"My Closet"` is two spellings
// of one name held by nothing — rename the closet and the tab silently wears Sparkles again. Keyed
// off the imported constant, a rename moves both ends at once and cannot desynchronise.
import { Sparkles, SquareStack, UserRound, BookOpen, Star, Music, Flower2, Flame, Feather, Shirt } from "lucide-react";
import type { ReactNode } from "react";
import { CLOSET_TITLE } from "../closet.js";

export const DRAWER_ICONS: Record<string, ReactNode> = {
  Shelves: <SquareStack size={19} strokeWidth={1.5} />,
  "Ask Lala": <UserRound size={19} strokeWidth={1.5} />,
  Journal: <BookOpen size={19} strokeWidth={1.5} />,
  Verses: <Star size={19} strokeWidth={1.5} />,
  Chants: <Music size={19} strokeWidth={1.5} />,
  Affirmations: <Flower2 size={19} strokeWidth={1.5} />,
  Incantations: <Flame size={19} strokeWidth={1.5} />,
  Angels: <Feather size={19} strokeWidth={1.5} />,
  // A wardrobe, which is what the room is — her word was "a proper closet/wardrobe icon, never the
  // Sparkles fallback".
  [CLOSET_TITLE]: <Shirt size={19} strokeWidth={1.5} />,
};

export function DrawerTab({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button className="drawer-tab" type="button" onClick={onClick} aria-label={label}>
      <span className="drawer-tab__icon" aria-hidden="true">{DRAWER_ICONS[label] || <Sparkles size={19} strokeWidth={1.5} />}</span>
      <span className="drawer-tab__label">{label}</span>
    </button>
  );
}
